import React from 'react';
import { Link } from 'react-router-dom';
import DreamMarque from './DreamMarque';
import { ReactComponent as DreamWordmark } from '../../icons/brand/dream-wordmark.svg';
import './public-notice-page.scss';

interface PublicNoticePageProps {
  title?: React.ReactNode;
  body?: React.ReactNode;
  /** Buttons / links rendered under the copy. */
  actions?: React.ReactNode;
  /** Show a spinner in place of the copy. */
  loading?: boolean;
  /** Line shown under the spinner, e.g. "Getting live prices". */
  loadingLabel?: React.ReactNode;
  /** `success` adds a Green check above the title. */
  tone?: 'neutral' | 'success';
  /** Where the logo links to; omit to render it as a static mark. */
  logoTo?: string;
  className?: string;
  testId?: string;
}

/**
 * Full-viewport notice used by public routes for their terminal states
 * (404, expired / invalid links, invite-only, load errors).
 */
const PublicNoticePage: React.FC<PublicNoticePageProps> = ({
  title,
  body,
  actions,
  loading = false,
  loadingLabel,
  tone = 'neutral',
  logoTo,
  className,
  testId,
}) => {
  const logo = (
    <>
      <DreamMarque className='public-notice__marque' />
      <DreamWordmark className='public-notice__wordmark' />
    </>
  );

  return (
    <div
      className={`public-notice${className ? ` ${className}` : ''}`}
      data-testid={testId}
    >
      <header className='public-notice__header'>
        {logoTo ? (
          <Link to={logoTo} className='public-notice__logo' aria-label='Dream'>
            {logo}
          </Link>
        ) : (
          <div className='public-notice__logo' aria-hidden='true'>
            {logo}
          </div>
        )}
      </header>
      <main className='public-notice__main'>
        {loading ? (
          <div className='public-notice__loading'>
            <div className='public-notice__spinner' role='status' />
            {loadingLabel && (
              <p className='public-notice__body'>{loadingLabel}</p>
            )}
          </div>
        ) : (
          <div className='public-notice__content'>
            {tone === 'success' && (
              <span className='public-notice__check' aria-hidden='true'>
                <svg viewBox='0 0 24 24' fill='none'>
                  <path
                    d='M5 12.5 9.5 17 19 7.5'
                    stroke='currentColor'
                    strokeWidth='2.2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </span>
            )}
            {title && <h1 className='public-notice__title'>{title}</h1>}
            {body && <p className='public-notice__body'>{body}</p>}
            {actions && <div className='public-notice__actions'>{actions}</div>}
          </div>
        )}
      </main>
    </div>
  );
};

export default PublicNoticePage;
